import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Chip, CircularProgress, Stack, Switch, Tooltip, Typography } from '@mui/material';
import { apiService } from '../../services/apiService';
import { TEMPLATE_STATUS, wa } from './waTheme';

interface Automation {
  key: string;
  label: string;
  when: string;
  templateKey: string;
  templateStatus: string;
  enabled: boolean;
}

/**
 * Automatic WhatsApp messages (welcome, pack purchased, class reminder …) —
 * one on/off each, OFF until the studio turns it on. A toggle only works once
 * that message's template is approved on the Templates tab.
 */
const AutomaticMessages: React.FC<{
  studioId: string;
  canWrite: boolean;
  notify: (msg: string, sev?: 'success' | 'error') => void;
}> = ({ studioId, canWrite, notify }) => {
  const [rows, setRows] = useState<Automation[] | null>(null);
  const [connected, setConnected] = useState(true);
  const [busy, setBusy] = useState<string | null>(null);
  // The parent's notify is a new function each render — keep it out of deps.
  const notifyRef = useRef(notify);
  notifyRef.current = notify;

  const load = useCallback(async () => {
    try {
      const res: any = await apiService.getAutomations(studioId);
      const data = res?.data ?? res;
      setRows(data?.automations ?? []);
      setConnected(data?.connected !== false);
    } catch (e: any) {
      setRows([]);
      notifyRef.current(e?.message ?? 'Could not load automatic messages', 'error');
    }
  }, [studioId]);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = async (a: Automation, enabled: boolean) => {
    setBusy(a.key);
    try {
      const res: any = await apiService.setAutomation(a.key, enabled, studioId);
      const data = res?.data ?? res;
      setRows(data?.automations ?? rows);
      notify(`${a.label} ${enabled ? 'on' : 'off'}`);
    } catch (e: any) {
      notify(e?.message ?? 'Could not update', 'error');
    } finally {
      setBusy(null);
    }
  };

  if (!rows) {
    return <Box sx={{ py: 3, textAlign: 'center' }}><CircularProgress size={22} sx={{ color: wa.teal }} /></Box>;
  }
  if (!rows.length) return null;

  return (
    <Box sx={{ mb: 3, borderRadius: 3, bgcolor: '#fff', border: '1px solid rgba(0,0,0,0.06)', overflow: 'hidden' }}>
      <Box sx={{ px: 2, py: 1.5, borderBottom: '1px solid rgba(0,0,0,0.06)' }}>
        <Typography fontWeight={800}>Automatic messages</Typography>
        <Typography variant="caption" color="text.secondary">
          {connected ? 'Sent by themselves when each event happens.' : 'Connect WhatsApp under Setup to use these.'}
        </Typography>
      </Box>
      {rows.map((a) => {
        const st = TEMPLATE_STATUS[a.templateStatus] ?? TEMPLATE_STATUS.not_registered;
        const approved = a.templateStatus === 'approved';
        // Turning OFF is always allowed; turning ON needs an approved template.
        const locked = !canWrite || !connected || (!approved && !a.enabled);
        return (
          <Stack
            key={a.key}
            direction="row"
            alignItems="center"
            spacing={2}
            sx={{ px: 2, py: 1.2, '&:not(:last-of-type)': { borderBottom: '1px solid rgba(0,0,0,0.04)' } }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography fontWeight={700} fontSize={14}>{a.label}</Typography>
              <Typography fontSize={12} color="text.secondary" noWrap>{a.when}</Typography>
            </Box>
            <Chip size="small" label={st.label} sx={{ height: 22, fontSize: 11, color: st.color, bgcolor: st.bg, fontWeight: 700 }} />
            <Tooltip title={!approved && !a.enabled ? 'Approve its template on the Templates tab first' : ''}>
              <span>
                {busy === a.key ? (
                  <Box sx={{ width: 58, textAlign: 'center' }}><CircularProgress size={18} sx={{ color: wa.teal }} /></Box>
                ) : (
                  <Switch
                    checked={a.enabled}
                    disabled={locked}
                    onChange={(_, v) => toggle(a, v)}
                    inputProps={{ 'aria-label': `${a.label} on/off` }}
                    sx={{ '& .Mui-checked': { color: `${wa.teal} !important` }, '& .Mui-checked + .MuiSwitch-track': { bgcolor: `${wa.green} !important` } }}
                  />
                )}
              </span>
            </Tooltip>
          </Stack>
        );
      })}
    </Box>
  );
};

export default AutomaticMessages;
